import React from "react";
import { motion } from "framer-motion";
import {
  FaShieldAlt,
  FaProjectDiagram,
  FaGlobe,
  FaMicrochip,
  FaUsers,
  FaBox,
} from "react-icons/fa";

const Services = () => {
  const services = [
    {
      icon: FaShieldAlt,
      title: "Smart Contract Development",
      description:
        "ERC-20/721, governance, DeFi primitives, audits-first approach on Ethereum.",
      color: "text-blue-500",
      bgColor: "bg-blue-50",
    },
    {
      icon: FaProjectDiagram,
      title: "Hyperledger Fabric Solutions",
      description:
        "Chaincode, network setup, identity management, and enterprise integration.",
      color: "text-green-500",
      bgColor: "bg-green-50",
    },
    {
      icon: FaGlobe,
      title: "DApp Frontends",
      description:
        "React/Next.js, wallet integrations, and delightful UX with Tailwind CSS.",
      color: "text-purple-500",
      bgColor: "bg-purple-50",
    },
    {
      icon: FaMicrochip,
      title: "APIs & Backends",
      description:
        "Node.js, Express, REST APIs, and data layers with MongoDB/MySQL.",
      color: "text-orange-500",
      bgColor: "bg-orange-50",
    },
    {
      icon: FaUsers,
      title: "Governance & Tokenomics",
      description:
        "DAO design, on-chain voting, incentive alignment and treasury considerations.",
      color: "text-red-500",
      bgColor: "bg-red-50",
    },
    {
      icon: FaBox,
      title: "DevOps & Delivery",
      description:
        "Dockerized deployments, CI/CD, and secure environment management.",
      color: "text-indigo-500",
      bgColor: "bg-indigo-50",
    },
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From smart contracts to full-stack integrations, I help teams ship
            secure, scalable decentralized solutions.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <motion.div
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="bg-white rounded-xl shadow-lg p-6 h-full hover:shadow-2xl transition-all duration-300 border border-gray-100"
                >
                  {/* Icon */}
                  <div
                    className={`w-16 h-16 ${service.bgColor} rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <service.icon className={`text-2xl ${service.color}`} />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                    {service.title}
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Hover Effect */}
                  <div className="mt-6 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="text-blue-600 font-medium hover:text-blue-700 transition-colors duration-300"
                    >
                      Learn More →
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Build Something Amazing?
            </h3>
            <p className="text-lg opacity-90 mb-6">
              Let's collaborate to create innovative blockchain solutions that
              drive real-world impact.
            </p>
            <motion.a
              href="#contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg"
            >
              <span>Get Started</span>
              <FaUsers />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
